import "./Implementation.css";
import CardColorBlock from "../../../components/cards/CardColorBlock/CardColorBlock";
import HighlightText from "../../../components/texts/HighlightText/HighlightText";
import Title from "../../../components/common/Title/Title";
import OptimizedImage from "../../../components/common/OptimizedImage/OptimizedImage";
import SEO from "../../../components/common/SEO/SEO";
import { useTranslation, Trans } from "react-i18next";

export default function Implementation() {
    const { t } = useTranslation(['implementation']);

    const seo = t('seo', { returnObjects: true }) || {};
    const header = t('header', { returnObjects: true }) || {};
    const cards = t('cards', { returnObjects: true }) || {};
    const sections = t('sections', { returnObjects: true }) || {};

    return (
        <>
            <SEO
                title={seo.title}
                description={seo.description}
                url={seo.url}
                image={seo.image}
            />
            <div className="main-container">
                <main className="head-content">
                    <div className="column-content-container height-container">
                        <Title text={header.title} size={["4rem", "3rem", "2rem"]} lineHeight={"1"} level="h1" />
                        <HighlightText text={header.tagline} size={["1.4rem", "1.2rem", "1rem"]} padding={["50px", "30px 24px", "20px"]} fontWeight="500" alignItems="flex-start" />
                        <p className="paragraph-content">
                            <Trans
                                i18nKey="header.paragraph"
                                ns="implementation"
                                components={[
                                    <a className="text-link" href="/expertise/industrial4-0" />
                                ]}
                            />
                        </p>
                    </div>
                    <div className="cards-container">
                        <CardColorBlock
                            image={"/images/icons/robot_arm_white.webp"}
                            color={"var(--color-orange)"}
                            title={cards.fullRealization?.title}
                            text={
                                <p>
                                    <Trans
                                        i18nKey="cards.fullRealization.text"
                                        ns="implementation"
                                        components={[<br />, <strong />]}
                                    />
                                </p>
                            }
                        />
                        <CardColorBlock
                            iconClassName="fas fa-industry"
                            iconStyle={{ fontSize: "70px", color: "white" }}
                            color={"var(--color-blue)"}
                            title={cards.existingAssets?.title}
                            text={cards.existingAssets?.text}
                        />
                        <CardColorBlock
                            iconClassName="fas fa-map-signs"
                            iconStyle={{ fontSize: "70px", color: "white" }}
                            color={"var(--color-blue)"}
                            title={cards.vendorIndependent?.title}
                            text={cards.vendorIndependent?.text}
                        />
                        <CardColorBlock
                            iconClassName="fas fa-unlock-alt"
                            iconStyle={{ fontSize: "70px", color: "white" }}
                            color={"var(--color-blue)"}
                            title={cards.open?.title}
                            text={cards.open?.text}
                        />
                    </div>
                </main>

                <section className="page-content-container">
                    <div className="row-content-container" style={{ alignItems: "center", gap: "0px" }}>
                        <div>
                            <Title text={sections.fullRealization?.title} size={["2rem", "1.8rem", "1.5rem"]} />
                            <p className="paragraph-content">
                                <Trans
                                    i18nKey="sections.fullRealization.body"
                                    ns="implementation"
                                    components={[<a className="text-link" href="/solutions/kickstarter" />]}
                                />
                            </p>
                        </div>
                        <OptimizedImage 
                            src="/images/industries/ICS.webp"
                            className="ICS-mask" 
                            alt="ICS"
                        />
                    </div>
                    <div className="row-content-container reverse-column" style={{ alignItems: "center", gap: "0px" }}>
                        <OptimizedImage 
                            src="/images/industries/container.webp"
                            className="containers-mask" 
                            alt="Containers"
                        />
                        <div className="column-content-container">
                            <Title text={sections.analysis?.title} size={["2rem", "1.8rem", "1.5rem"]} />
                            <span className="implementation-quotes-text">
                                <Trans
                                    i18nKey="sections.analysis.quote"
                                    ns="implementation"
                                    components={[<strong />]}
                                />
                            </span>
                            <p className="paragraph-content">
                                <Trans
                                    i18nKey="sections.analysis.body"
                                    ns="implementation"
                                    components={[
                                        <a className="text-link" href="/solutions/kickstarter" />
                                    ]}
                                />
                            </p>
                            <p className="paragraph-content">
                                <Trans
                                    i18nKey="sections.analysis.closing"
                                    ns="implementation"
                                    components={[
                                        <a className="text-link" href="/solutions/kickstarter" />
                                    ]}
                                />
                            </p>
                        </div>
                    </div>
                    <div className="row-content-container" style={{ alignItems: "center", gap: "0px" }}>
                        <div className="column-content-container">
                            <Title text={sections.technicalDesign?.title} size={["2rem", "1.8rem", "1.5rem"]} />
                            <span className="implementation-quotes-text">{sections.technicalDesign?.quote}</span>
                            <p className="paragraph-content">
                                <Trans
                                    i18nKey="sections.technicalDesign.body"
                                    ns="implementation"
                                    components={[
                                        <strong />,
                                        <a className="text-link" href="/expertise/iiot" />,
                                        <a className="text-link" href="/expertise/industrial4-0" />
                                    ]}
                                />
                            </p>
                        </div>
                        <OptimizedImage
                            src="/images/industries/robot_arm_yellow.webp"
                            className="robot-arm-mask"
                            alt="Robot Arm"
                        />
                    </div>
                    <div className="row-content-container reverse-column" style={{ alignItems: "center", gap: "0px" }}>
                        <OptimizedImage
                            src="/images/industries/shutterstock.webp"
                            className="shutterstock-mask"
                            alt="Shutterstock"
                        />
                        <div className="column-content-container">
                            <Title text={sections.realization?.title} size={["2rem", "1.8rem", "1.5rem"]} />
                            <span className="implementation-quotes-text">{sections.realization?.quote}</span>
                            <p className="paragraph-content">{sections.realization?.body}</p>
                        </div>
                    </div>
                    <div className="row-content-container" style={{ alignItems: "center", gap: "0px" }}>
                        <div className="column-content-container">
                            <Title text={sections.organisationalChange?.title} size={["2rem", "1.8rem", "1.5rem"]} />
                            <span className="implementation-quotes-text">{sections.organisationalChange?.quote}</span>
                            <p className="paragraph-content">{sections.organisationalChange?.body}</p>
                        </div>
                        <OptimizedImage
                            src="/images/industries/tool.webp"
                            className="tool-mask"
                            alt="Tool"
                        />
                    </div>
                    <div className="row-content-container reverse-column" style={{ alignItems: "center", gap: "0px" }}>
                        <OptimizedImage
                            src="/images/industries/pc.webp"
                            className="pc-mask"
                            alt="PC"
                        />
                        <div className="column-content-container">
                            <Title text={sections.support?.title} size={["2rem", "1.8rem", "1.5rem"]} />
                            <span className="implementation-quotes-text">{sections.support?.quote}</span>
                            <p className="paragraph-content">
                                <Trans
                                    i18nKey="sections.support.body"
                                    ns="implementation"
                                    components={[
                                        <a className="text-link" href="/support" />
                                    ]}
                                />
                            </p>
                            <p className="paragraph-content">
                                <Trans
                                    i18nKey="sections.support.closing"
                                    ns="implementation"
                                    components={[
                                        <a className="text-link" href="/support" />
                                    ]}
                                />
                            </p>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
}